import { v4 as uuid } from 'uuid';
import { create } from 'zustand';

export interface TextStyle {
  color?: string;
  backgroundColor?: string;
  fontSize?: number;
  fontWeight?: string | number;
  fontFamily?: string;
  textAlign?: string;
  textOutlineWidth?: number;
  textOutlineColor?: string;
  borderWidth?: number;
  borderColor?: string;
  objectFit?: string;
  removeBackground?: boolean;
  blur?: number;
  opacity?: number;
  animation?: string;
  padding?: number;
  borderRadius?: number;
  imageSearchMode?: 'keyword' | 'prompt';
}

export interface EditorItem {
  id: string;
  type: 'text' | 'image' | 'auto-image' | 'auto-image-prompt' | 'ai-text' | 'variable';
  content: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  style: TextStyle;
  zIndex: number;
  prompt?: string;
}

export type EditorState = {
  items: EditorItem[];
  selectedItemId: string | null;
  editingItemId: string | null;
  addItem: (item: Omit<EditorItem, 'id'>) => void;
  updateItem: (id: string, updates: Partial<EditorItem>) => void;
  deleteItem: (id: string) => void;
  selectItem: (id: string | null) => void;
  setEditingItem: (id: string | null) => void;
  duplicateItem: (id: string) => void;
  copyItem: (id: string) => void;
  pasteItem: () => void;
  clearAll: () => void;
  setItems: (items: EditorItem[]) => void;
};

export const useEditorStore = create<EditorState>((set, get) => ({
  items: [],
  selectedItemId: null,
  editingItemId: null,
  
  addItem: (item) => set((state) => ({
    items: [...state.items, { ...item, id: uuid() }],
  })),
  
  updateItem: (id, updates) => set((state) => ({
    items: state.items.map((item) =>
      item.id === id ? { ...item, ...updates } : item
    ),
  })),
  
  deleteItem: (id) => set((state) => ({
    items: state.items.filter((item) => item.id !== id),
    selectedItemId: state.selectedItemId === id ? null : state.selectedItemId,
  })),
  
  selectItem: (id) => set({ selectedItemId: id }),
  
  setEditingItem: (id) => set({ editingItemId: id }),
  
  duplicateItem: (id) => {
    const { items } = get();
    const itemToDuplicate = items.find(item => item.id === id);
    
    if (itemToDuplicate) {
      const newItem = {
        ...itemToDuplicate,
        id: uuid(),
        position: {
          x: itemToDuplicate.position.x + 20,
          y: itemToDuplicate.position.y + 20
        }
      };
      
      set((state) => ({
        items: [...state.items, newItem],
        selectedItemId: newItem.id
      }));
    }
  },
  
  copyItem: (id) => {
    const { items } = get();
    const itemToCopy = items.find(item => item.id === id);
    
    if (itemToCopy) {
      // Store in localStorage to persist between page refreshes
      localStorage.setItem('copiedItem', JSON.stringify(itemToCopy));
    }
  },
  
  pasteItem: () => {
    const copiedItemJson = localStorage.getItem('copiedItem');
    
    if (copiedItemJson) {
      try {
        const copiedItem = JSON.parse(copiedItemJson);
        
        const newItem = {
          ...copiedItem,
          id: uuid(),
          position: {
            x: copiedItem.position.x + 20,
            y: copiedItem.position.y + 20
          }
        };
        
        set((state) => ({
          items: [...state.items, newItem],
          selectedItemId: newItem.id
        }));
      } catch (error) {
        console.error('Failed to paste item:', error);
      }
    }
  },
  
  clearAll: () => set({
    items: [],
    selectedItemId: null,
  }),
  
  setItems: (items) => set({ items }),
})); 